import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, delay, map, switchMap, withLatestFrom } from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { TaskRequester } from '../../requesters/core/task.requester';
import { AppTaskModalComponent } from '../../ui/components/app-task-modal/app-task-modal.component';
import { ProjectStorage } from '../project.storage';
import { TaskAdapter } from '../task.adapter';
import {
	clearCacheJobs,
	clearCacheJobsFail,
	clearCacheJobsSuccess,
	fetchTaskFail,
	fetchTaskSuccess,
	initTaskList,
	killAllJobs,
	killAllJobsFail,
	killAllJobsSuccess,
	killJob,
	killJobFail,
	killJobSuccess,
	openTaskModal,
	refreshTaskList,
	toggleJobsCacheActive,
	toggleJobsCacheActiveFail,
	updateViewActiveJobs,
} from './task.actions';
import { TaskSelector } from './task.selector';

@Injectable()
export class TaskEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	// ##########################################################
	// Business Effects
	// ##########################################################
	public initTask$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initTaskList),
			withLatestFrom(this.taskSelector.getViewActiveJobs$()),
			switchMap(([, viewActiveJobs]) =>
				combineAll({
					types: this.taskRequester.getAvailableTaskTypes(),
					tasks: this.taskRequester.getTaskList(viewActiveJobs, true),
				})
			),
			map((result) =>
				fetchTaskSuccess({
					taskList: this.taskAdapter.generateTaskList(
						result.tasks,
						result.types
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchTaskFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshTask$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshTaskList),
			withLatestFrom(this.taskSelector.getViewActiveJobs$()),
			switchMap(([, viewActiveJobs]) =>
				combineAll({
					types: this.taskRequester.getAvailableTaskTypes(),
					tasks: this.taskRequester.getTaskList(viewActiveJobs, false),
				})
			),
			map((result) =>
				fetchTaskSuccess({
					taskList: this.taskAdapter.generateTaskList(
						result.tasks,
						result.types
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchTaskFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateViewActiveJobs$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateViewActiveJobs),
			map(() => refreshTaskList())
		)
	);

	public toggleJobsCacheActive$ = createEffect(() =>
		this.actions$.pipe(
			ofType(toggleJobsCacheActive),
			map((action) => {
				this.storage.setIsJobsCacheActive(action.activeCache);
				return refreshTaskList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(toggleJobsCacheActiveFail(error));
				return caught;
			})
		)
	);

	public killAllJobs$ = createEffect(() =>
		this.actions$.pipe(
			ofType(killAllJobs),
			switchMap(() => this.taskRequester.killAllJobs()),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@task.action.kill.all.success:`
				);
				return killAllJobsSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@task.action.kill.all.fail:`
				);
				this.store.dispatch(
					killAllJobsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public clearCacheJobs$ = createEffect(() =>
		this.actions$.pipe(
			ofType(clearCacheJobs),
			switchMap(() => this.taskRequester.clearCacheJobs()),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@task.action.clear.cache.success:`
				);
				return clearCacheJobsSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@task.action.clear.cache.fail:`
				);
				this.store.dispatch(
					clearCacheJobsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public KillJob$ = createEffect(() =>
		this.actions$.pipe(
			ofType(killJob),
			switchMap((action) => this.taskRequester.killJob(action.jobId)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@task.action.kill.success:`
				);
				return killJobSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@task.action.kill.fail:`
				);
				this.store.dispatch(
					killJobFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public returnToTaskList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(killAllJobsSuccess, clearCacheJobsSuccess),
			map(() => refreshTaskList())
		)
	);

	public returnToTaskListAfterKill$ = createEffect(() =>
		this.actions$.pipe(
			ofType(killJobSuccess),
			delay(5000),
			map(() => refreshTaskList())
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public openTaskModal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openTaskModal),
				switchMap(() => {
					const modalRef = this.modalService.openModal(
						AppTaskModalComponent,
						{
							title: $localize`:i18n=@@task.list.modal.title:`,
							subTitle: '',
						},
						{
							disableClose: true,
						}
					);
					return modalRef.afterClosed();
				})
			),
		{
			dispatch: false,
		}
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly modalService: AppModalService,
		private readonly taskRequester: TaskRequester,
		private readonly taskSelector: TaskSelector,
		private readonly taskAdapter: TaskAdapter,
		private readonly storage: ProjectStorage,
		private readonly store: Store,
		private readonly ngCommService: NgCommService
	) {}
}
